import styled from "@emotion/styled";
import { Box, IconButton, Theme } from "@mui/material";
import Badge from "@mui/material/Badge";
import {
  RESPONSIVE_DRAWER,
  shouldForwardProp,
} from "../../common/Common.style";

const RAIL_WIDTH = 56;
const CHATLIST_WIDTH = 385;
const FULL_PANEL_WIDTH = 385;

// LeftPanelBox is the LEFT column.
// >= 1280: full 385 px panel (today's UX).
// <  1280: only one of {chatlist, rail} is visible at a time. The rail
//          appears only when chatlist is hidden (= chat info is open).
//          Both children are always mounted; the rail's width transitions
//          0 ↔ 56 in lockstep with the box width so the chatlist gets
//          smoothly revealed / clipped as the rail grows/shrinks.
export const LeftPanelBox = styled(Box, {
  shouldForwardProp: prop =>
    shouldForwardProp<{ $chatlistOpen?: boolean }>(["$chatlistOpen"], prop),
})<{ $chatlistOpen?: boolean }>(({ theme, $chatlistOpen }) => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  borderRight: `1px solid ${(theme as Theme).palette.divider}`,
  boxShadow: "2px 0 4px -2px rgba(0, 0, 0, 0.15)",
  zIndex: 1,
  transition: "width 0.18s ease, min-width 0.18s ease, max-width 0.18s ease",
  [`@media (min-width: ${RESPONSIVE_DRAWER.WIDE_BP}px)`]: {
    minWidth: `${FULL_PANEL_WIDTH}px`,
    maxWidth: `${FULL_PANEL_WIDTH}px`,
    width: `${FULL_PANEL_WIDTH}px`,
  },
  [`@media (max-width: ${RESPONSIVE_DRAWER.WIDE_BP - 1}px)`]: {
    flexDirection: "row",
    overflow: "hidden",
    minWidth: $chatlistOpen ? `${CHATLIST_WIDTH}px` : `${RAIL_WIDTH}px`,
    maxWidth: $chatlistOpen ? `${CHATLIST_WIDTH}px` : `${RAIL_WIDTH}px`,
    width: $chatlistOpen ? `${CHATLIST_WIDTH}px` : `${RAIL_WIDTH}px`,
  },
}));

export const StyledBadge = styled(Badge)<{ count: number }>(
  ({ count, theme }) => ({
    right: count > 9999 ? 5 : 0,
    top: 0,
    padding: "6px 0px 0px 0px",
  }),
);

export const LeftPanelTabWrapper = styled(Box)`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

export const TabsContainer = styled(Box)`
  border-bottom: 1px;
  border-color: ${({ theme }) => (theme as Theme).palette.divider};
`;

// === Compact rail ===
// Always mounted at narrow widths but collapses to width 0 when the chatlist
// is open

type RailContainerProps = { $collapsed?: boolean };

export const RailContainer = styled(Box, {
  shouldForwardProp: prop =>
    shouldForwardProp<RailContainerProps>(["$collapsed"], prop),
})<RailContainerProps>(({ theme, $collapsed }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  width: $collapsed ? 0 : RAIL_WIDTH,
  flexShrink: 0,
  height: "100%",
  padding: $collapsed ? 0 : "10px 0",
  gap: "4px",
  borderRight: $collapsed
    ? "none"
    : `1px solid ${(theme as Theme).palette.divider}`,
  overflow: "hidden",
  transition: "width 0.18s ease, padding 0.18s ease",
}));

type RailIconButtonProps = { isActive?: boolean };

export const RailIconButton = styled(IconButton, {
  shouldForwardProp: prop =>
    shouldForwardProp<RailIconButtonProps>(["isActive"], prop),
})<RailIconButtonProps>(({ theme, isActive }) => ({
  width: 48,
  height: 48,
  borderRadius: 8,
  padding: 0,
  backgroundColor: isActive
    ? (theme as Theme).palette.background.paper
    : "transparent",
  color: isActive
    ? (theme as Theme).palette.primary.main
    : (theme as Theme).palette.text.primary,
  "&:hover": {
    backgroundColor: (theme as Theme).palette.background.paper,
  },
}));

// Chatlist column rendered when the user opens it from the rail. Sibling of
// the rail in flex-row order — push behaviour, no overlay (issue #1240).
export const ChatlistPanelContainer = styled(Box)(({ theme }) => ({
  width: `${CHATLIST_WIDTH}px`,
  height: "100%",
  flexShrink: 0,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  background: (theme as Theme).palette.background.default,
}));
